import { Component, Inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConsentModalComponent } from '@core/components/consent-modal/consent-modal.component';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { from, of, ReplaySubject } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { ConsentService } from '@core/data/consent.service';
import { restrictedUrls } from '@core/constants/app';
import { LocalUserService } from '@core/data/localUser.service';
import { CustomUserClaims } from '@core/interfaces/entities';


@Component({
  selector: 'app-pages',
  template: `<router-outlet></router-outlet>`,
})
export class PagesComponent {
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private router: Router,
    private consentService: ConsentService,
    private modalService: NgbModal,
    private localUserService: LocalUserService) {
    this.router.events.pipe(
      takeUntil(this.destroyed$),
      filter(event => event instanceof NavigationEnd && !restrictedUrls.includes((event as NavigationEnd).url)),
      switchMap(() => from(this.oktaAuth.getUser())),
      tap((user: CustomUserClaims) => {
        if (user.msesDataConsent !== undefined) {
          this.unsubscribeFromConsentModal();
        }
      }),
      filter((user: CustomUserClaims) => user.msesDataConsent === undefined),
      switchMap((user: CustomUserClaims) => {
        const modalRef = this.modalService.open(ConsentModalComponent, { centered: true, backdrop: false });
        modalRef.componentInstance.userData = user;
        return modalRef.componentInstance.selectionComplete;
      }),
      mergeMap((userSelection: { phoneNumber: number;
        hasAcceptedConsent: boolean; }) => from(this.oktaAuth.myaccount.getProfile()).pipe(map(({ profile }) => ({
        userSelection,
        profile,
      })))),
      mergeMap(({ userSelection, profile }) => {
        if (userSelection.hasAcceptedConsent){
          localUserService.recordUserData({phoneNumber: userSelection.phoneNumber});
        }
        profile.msesDataConsent = userSelection.hasAcceptedConsent;
        this.oktaAuth.myaccount.updateProfile({
          accessToken: this.oktaAuth.tokenManager.getTokensSync().accessToken.accessToken,
          payload: { profile: { ...profile, ...{ msesDataConsent: userSelection.hasAcceptedConsent } } },
        });
        return of(userSelection);
      }),
      mergeMap((userSelection) => this.consentService.recordConsent(userSelection)),
      catchError(() => of(null)),
    )
      .subscribe({ next: () => this.unsubscribeFromConsentModal() });
  }

  private unsubscribeFromConsentModal() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
